/** One artifact in full — report, dataset, dataflow, dashboard or app.
 *
 *  Before this, only reports could be opened. The other four were counts on
 *  Workspace 360 that led nowhere, so the evidence behind them was
 *  unreachable: who can read a dataset, what it connects to, whether it
 *  refreshes, and what changed on it.
 *
 *  One page serves all five rather than four more bespoke ones. The API
 *  returns a uniform shape and tells us which sections can hold data for the
 *  type in hand, so a tab is hidden when it is empty *by design* — a dashboard
 *  has no refresh history because dashboards do not refresh — and shown with
 *  an explanation when it is empty because nothing was collected. Those two
 *  cases look identical if you only check for an empty array, and conflating
 *  them is how a monitoring tool ends up quietly asserting things it does not
 *  know.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  fetchArtifactDetail,
  type ArtifactDetail,
  type ArtifactPerson,
} from "../api/client";
import PageHeader from "../components/PageHeader";
import Tabs, { type TabItem } from "../components/Tabs";
import DataTable from "../components/DataTable";
import StatCard from "../components/StatCard";
import ErrorBanner from "../components/ErrorBanner";
import EmptyState from "../components/EmptyState";
import { LoadingPanel } from "../components/Spinner";
import ShowSystemToggle from "../components/ShowSystemToggle";
import ChangeCategoryBadge from "../components/ChangeCategoryBadge";
import ChangeSourceBadge from "../components/ChangeSourceBadge";
import ChangeActor from "../components/ChangeActor";
import CollectionRequest from "../components/CollectionRequest";
import { useRoles } from "../auth/access";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

type TabId =
  | "properties"
  | "people"
  | "datasources"
  | "refresh"
  | "changes"
  | "exposure"
  | "subscriptions"
  | "reports";

const TAB_LABELS: Record<TabId, string> = {
  properties: "Properties",
  people: "People & Access",
  datasources: "Data Sources",
  refresh: "Refresh",
  changes: "Changes",
  exposure: "Exposure",
  subscriptions: "Subscriptions",
  reports: "Reports Using It",
};

function roleTone(role: string): string {
  const r = role.toLowerCase();
  if (r === "admin" || r === "owner")
    return "bg-danger-subtle text-danger-fg border-danger-border";
  if (r === "member" || r === "contributor" || r === "readwrite")
    return "bg-warning-subtle text-warning-fg border-warning-border";
  return "bg-surface-hover text-content-muted border-edge";
}

// Kept in step with StatusBadge's vocabulary. "disabled" used to be danger
// here and neutral there, so the same stored refresh row changed meaning
// depending on which page you opened it from.
function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (s === "failed" || s === "failure" || s === "error") return "text-danger-fg";
  if (s === "completed" || s === "success") return "text-success-fg";
  return "text-content-muted";
}

/** Connection details arrive as an object whose keys vary by source type. */
function describeConnection(details: unknown): string {
  if (!details || typeof details !== "object") return "—";
  const d = details as Record<string, unknown>;
  const parts = ["server", "database", "url", "path", "connectionString"]
    .map((k) => d[k])
    .filter((v) => typeof v === "string" && v)
    .map(String);
  if (parts.length > 0) return parts.join(" · ");
  const entries = Object.entries(d).filter(([, v]) => v !== null && v !== "");
  if (entries.length === 0) return "—";
  return entries.map(([k, v]) => `${k}: ${String(v)}`).join(" · ");
}

export default function ArtifactDetailPage() {
  const {
    workspaceId = "",
    artifactType = "",
    artifactId = "",
  } = useParams<{
    workspaceId: string;
    artifactType: string;
    artifactId: string;
  }>();
  const navigate = useNavigate();
  const { isMon } = useRoles();

  // The shared formatter, not a local new Date(): it honours the user's
  // timezone and date-format preferences, and it understands that the
  // activity table's sort key is a timestamp with a discriminator appended.
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [data, setData] = useState<ArtifactDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("properties");
  const [showSystem, setShowSystem] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actorEmail, setActorEmail] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchArtifactDetail(workspaceId, artifactType, artifactId, showSystem, {
      dateFrom,
      dateTo,
      actorEmail,
    })
      .then(setData)
      .catch((e) => setError(e?.message ?? "Failed to load artifact."))
      .finally(() => setLoading(false));
  }, [workspaceId, artifactType, artifactId, showSystem, dateFrom, dateTo, actorEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const peopleColumns: ColumnDef<ArtifactPerson, string>[] = [
    {
      accessorKey: "displayName",
      header: "Name",
      cell: ({ row }) => row.original.displayName || row.original.email || "—",
    },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ getValue }) => {
        const role = String(getValue() || "");
        if (!role) return <span className="text-content-muted">—</span>;
        return (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${roleTone(role)}`}
          >
            {role}
          </span>
        );
      },
    },
    {
      accessorKey: "principalType",
      header: "Type",
      cell: ({ getValue }) => getValue() || "—",
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changeColumns: ColumnDef<Record<string, any>, any>[] = [
    {
      accessorKey: "timestampActivity",
      header: "Date / Time",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.changeSource ?? "",
      cell: ({ row }) => <ChangeSourceBadge changeSource={row.original.changeSource} />,
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row) => row.changeCategory ?? "",
      cell: ({ row }) => (
        <ChangeCategoryBadge changeCategory={row.original.changeCategory} />
      ),
    },
    {
      id: "what",
      header: "What Changed",
      accessorFn: (row) => row.activityType ?? row.changeType ?? "",
      cell: ({ getValue }) => getValue() || "—",
    },
    {
      id: "who",
      header: "Who",
      accessorFn: (row) => row.actorEmail || (row.attributedActors ?? []).join(", ") || row.actorName || "",
      cell: ({ row }) => <ChangeActor {...row.original} />,
    },
    {
      id: "detail",
      header: "Detail",
      accessorFn: (row) => row.detail ?? row.subject ?? "",
      cell: ({ getValue }) => (
        <span className="text-xs text-content-muted">{getValue() || "—"}</span>
      ),
    },
  ];

  if (loading) return <LoadingPanel label="Loading artifact…" />;

  if (error && !data) {
    return (
      <div className="space-y-4">
        <PageHeader title="Artifact" />
        <ErrorBanner message={error} onRetry={load} />
      </div>
    );
  }

  if (!data) return null;

  // The API says which sections are meaningful for this type; the rest are
  // hidden rather than shown empty.
  const available = new Set(data.sections);
  const tabs: readonly TabItem<TabId>[] = (
    [
      "properties",
      "people",
      "datasources",
      "refresh",
      "changes",
      "exposure",
      "subscriptions",
      "reports",
    ] as TabId[]
  )
    .filter((t) => available.has(t))
    .map((t) => ({
      id: t,
      label: TAB_LABELS[t],
      count:
        t === "people"
          ? data.people.length
          : t === "datasources"
            ? data.datasources.length
            : t === "changes"
              ? data.changes.length
              : t === "exposure"
                ? data.publicExposure.length
                : t === "subscriptions"
                  ? data.subscriptions.length
                  : t === "reports"
                    ? data.relatedReports.length
                    : undefined,
    }));

  // A tab pre-selected from a previous artifact may not exist on this one.
  const activeTab = available.has(tab) ? tab : (tabs[0]?.id ?? "properties");

  const accessKnown = data.permissionsCollected || data.people.length > 0;
  const lastRefresh = data.refreshHistory[0];

  return (
    <div className="space-y-4">
      <PageHeader
        title={data.name}
        breadcrumb={[
          { label: "Workspaces", to: "/workspaces" },
          {
            label: data.workspaceName || "Workspace",
            to: `/workspaces/${data.workspaceId}`,
          },
          { label: data.name },
        ]}
        actions={
          <div className="flex items-center gap-3">
            {data.webUrl && (
              <a
                href={data.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-edge bg-surface-raised px-3 py-1.5 text-sm font-medium text-content hover:bg-surface-hover"
              >
                Open in Power BI
              </a>
            )}
            <ShowSystemToggle show={showSystem} onChange={setShowSystem} />
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
        <span className="inline-flex items-center rounded-full border border-edge bg-surface-raised px-2 py-0.5 text-xs font-semibold capitalize text-content">
          {data.artifactType}
        </span>
        {data.source === "live" && (
          <span className="inline-flex items-center rounded-full border border-info-border bg-info-subtle px-2 py-0.5 text-xs font-semibold text-info-fg">
            Live read
          </span>
        )}
        <code className="text-xs">{data.artifactId}</code>
      </div>

      {error ? <ErrorBanner message={error} onRetry={load} tone="warning" /> : null}

      {data.note ? (
        <div className="rounded-xl border border-info-border bg-info-subtle px-4 py-3 text-sm text-info-fg">
          {data.note}
        </div>
      ) : null}

      {/* The note above says what is missing; this offers to go and get it.
          Queueing a collection is an action on the tenant and is not part of
          the monitored-only role's surface, so for that role the button would
          only ever 403 — the note stays, the offer goes. */}
      {!isMon && (data.source === "live" || data.note) ? (
        <CollectionRequest
          workspaceId={workspaceId}
          artifactType={artifactType}
          artifactId={artifactId}
          artifactName={data.name}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="People with access"
          value={accessKnown ? data.people.length : "—"}
          onClick={() => setTab("people")}
        />
        {available.has("datasources") && (
          <StatCard
            label="Data sources"
            value={data.datasources.length}
            onClick={() => setTab("datasources")}
          />
        )}
        <StatCard
          label="Changes"
          value={data.changes.length}
          onClick={() => setTab("changes")}
        />
        {available.has("exposure") && (
          <StatCard
            label="Publicly shared"
            value={data.publicExposure.length}
            severity={data.publicExposure.length > 0 ? "critical" : "normal"}
            onClick={() => setTab("exposure")}
          />
        )}
      </div>

      <Tabs tabs={tabs} active={activeTab} onChange={setTab} />

      {activeTab === "properties" &&
        (data.properties.length === 0 ? (
          <EmptyState
            title="No stored properties"
            description="Nothing has been collected for this artifact, so it is known only by the id another record referenced."
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-xl border border-edge bg-surface-raised p-5">
              <h2 className="mb-3 text-sm font-semibold text-content">Properties</h2>
              <dl className="space-y-2 text-sm">
                {data.properties.map((p) => (
                  <div key={p.label} className="flex justify-between gap-4">
                    <dt className="text-content-muted">{p.label}</dt>
                    <dd className="break-all text-right font-medium text-content">
                      {p.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            {(data.refreshSchedule || lastRefresh || data.usage) && (
              <section className="rounded-xl border border-edge bg-surface-raised p-5">
                <h2 className="mb-3 text-sm font-semibold text-content">Health</h2>
                <dl className="space-y-2 text-sm">
                  {lastRefresh && (
                    <>
                      <div className="flex justify-between gap-4">
                        <dt className="text-content-muted">Last refresh</dt>
                        <dd className="font-medium text-content">
                          {formatDate(lastRefresh.refreshStartTime)}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-content-muted">Last status</dt>
                        <dd
                          className={`font-semibold ${statusTone(String(lastRefresh.refreshStatus ?? ""))}`}
                        >
                          {lastRefresh.refreshStatus ?? "—"}
                        </dd>
                      </div>
                    </>
                  )}
                  {data.refreshSchedule && (
                    <>
                      <div className="flex justify-between gap-4">
                        <dt className="text-content-muted">Schedule</dt>
                        <dd className="font-medium text-content">
                          {data.refreshSchedule.enabled ? "Enabled" : "Disabled"}
                        </dd>
                      </div>
                      {Array.isArray(data.refreshSchedule.days) &&
                        data.refreshSchedule.days.length > 0 && (
                          <div className="flex justify-between gap-4">
                            <dt className="text-content-muted">Days</dt>
                            <dd className="text-right font-medium text-content">
                              {data.refreshSchedule.days.join(", ")}
                            </dd>
                          </div>
                        )}
                      {Array.isArray(data.refreshSchedule.times) &&
                        data.refreshSchedule.times.length > 0 && (
                          <div className="flex justify-between gap-4">
                            <dt className="text-content-muted">Times</dt>
                            <dd className="text-right font-medium text-content">
                              {data.refreshSchedule.times.join(", ")}
                            </dd>
                          </div>
                        )}
                    </>
                  )}
                  {data.usage && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-content-muted">Last accessed</dt>
                      <dd className="font-medium text-content">
                        {data.usage.lastAccessedDate
                          ? formatDate(String(data.usage.lastAccessedDate))
                          : "Never recorded"}
                      </dd>
                    </div>
                  )}
                  {data.permissionSnapshotAt && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-content-muted">Access last snapshotted</dt>
                      <dd className="font-medium text-content">
                        {formatDate(data.permissionSnapshotAt)}
                      </dd>
                    </div>
                  )}
                </dl>
              </section>
            )}
          </div>
        ))}

      {activeTab === "people" && (
        <div className="space-y-3">
          {data.people.length === 0 ? (
            <EmptyState
              title={data.permissionsCollected ? "Nobody has access" : "Access not known"}
              description={
                data.permissionsCollected
                  ? "A permission snapshot exists for this artifact and it lists no users."
                  : "No permission snapshot has been collected for this artifact, so who can reach it is unknown rather than nobody."
              }
            />
          ) : (
            <DataTable data={data.people} columns={peopleColumns} initialPageSize={25} />
          )}

          {/* What moved at the last snapshot: the access-change evidence SOX
              reviews turn on, and it is only meaningful next to the list. */}
          {(data.accessChanges.added?.length ||
            data.accessChanges.removed?.length ||
            data.accessChanges.roleChanged?.length) ? (
            <section className="rounded-xl border border-edge bg-surface-raised p-5">
              <h2 className="mb-3 text-sm font-semibold text-content">
                Changes at the last snapshot
              </h2>
              <div className="space-y-2 text-sm">
                {data.accessChanges.added?.map((p) => (
                  <div key={`a-${p.email}`} className="text-success-fg">
                    + {p.displayName || p.email} ({p.role || "no role"})
                  </div>
                ))}
                {data.accessChanges.removed?.map((p) => (
                  <div key={`r-${p.email}`} className="text-danger-fg">
                    − {p.displayName || p.email} ({p.role || "no role"})
                  </div>
                ))}
                {data.accessChanges.roleChanged?.map((p) => (
                  <div key={`c-${p.email}`} className="text-warning-fg">
                    ~ {p.displayName || p.email}: {p.previousRole} → {p.newRole}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {activeTab === "datasources" &&
        (data.datasources.length === 0 ? (
          <EmptyState
            title="No data sources recorded"
            description="Nothing has been collected about what this artifact connects to. For a dataset or report this is collected only for monitored workspaces."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {data.datasources.map((ds, i) => (
              <section
                key={String(ds.datasourceId || i)}
                className="rounded-xl border border-edge bg-surface-raised p-4"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-content">
                    {ds.datasourceType || "Unknown type"}
                  </h3>
                  {ds.gatewayId ? (
                    <button
                      type="button"
                      onClick={() => navigate("/gateways")}
                      className="rounded-full border border-warning-border bg-warning-subtle px-2 py-0.5 text-xs font-semibold text-warning-fg"
                      title="Reaches on-premises data through a gateway"
                    >
                      {ds.gatewayName || "Gateway"}
                    </button>
                  ) : (
                    <span className="text-xs text-content-muted">Cloud</span>
                  )}
                </div>
                <p className="break-all text-xs text-content-muted">
                  {describeConnection(ds.connectionDetails)}
                </p>
              </section>
            ))}
          </div>
        ))}

      {activeTab === "refresh" &&
        (data.refreshHistory.length === 0 ? (
          <EmptyState
            title="No refresh history"
            description="No refresh runs have been recorded for this artifact, from either the refresh API or the audit log. This is not a claim that it never refreshed: a model outside a dedicated capacity, in a workspace the collector cannot read, leaves no trace in either source."
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-edge bg-surface-raised">
            <table className="min-w-full divide-y divide-edge text-sm">
              <thead className="bg-surface-sunken">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-content">Started</th>
                  <th className="px-4 py-2 text-left font-semibold text-content">Ended</th>
                  <th className="px-4 py-2 text-left font-semibold text-content">Status</th>
                  <th className="px-4 py-2 text-left font-semibold text-content">Type</th>
                  <th className="px-4 py-2 text-left font-semibold text-content">Evidence</th>
                  <th className="px-4 py-2 text-left font-semibold text-content">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {data.refreshHistory.map((r, i) => {
                  // Audit-log rows are stamped when the outcome was written,
                  // which is the end of the run rather than its start, and
                  // they carry no duration. Labelling the row is what stops
                  // that timestamp being read as a start time.
                  const fromAudit = r.source === "auditLog";
                  return (
                    <tr key={String(r.refreshStartTime || i)}>
                      <td className="px-4 py-2 text-content-muted">
                        {fromAudit ? "~" : ""}
                        {formatDate(r.refreshStartTime)}
                      </td>
                      <td className="px-4 py-2 text-content-muted">
                        {r.refreshEndTime ? formatDate(r.refreshEndTime) : "—"}
                      </td>
                      <td
                        className={`px-4 py-2 font-semibold ${statusTone(String(r.refreshStatus ?? ""))}`}
                      >
                        {r.refreshStatus ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-content-muted">
                        {r.refreshType ?? "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className="text-xs text-content-muted"
                          title={
                            fromAudit
                              ? "From the audit log: completion time only, no duration or error detail"
                              : "From the Power BI refresh API: exact start, end and failure detail"
                          }
                        >
                          {fromAudit ? "Audit log" : "Refresh API"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-danger-fg">
                        {r.errorMessage ?? ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

      {activeTab === "changes" && (
        <div className="space-y-3">
          {/* Server-side, because the date range narrows the DynamoDB sort key
              rather than trimming rows already fetched. */}
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-edge bg-surface-raised p-4">
            <label className="flex flex-col gap-1 text-xs text-content-muted">
              From
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border border-edge bg-surface px-2 py-1 text-sm text-content"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-content-muted">
              To
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border border-edge bg-surface px-2 py-1 text-sm text-content"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-content-muted">
              Actor email
              <input
                type="text"
                value={actorEmail}
                onChange={(e) => setActorEmail(e.target.value)}
                placeholder="someone@torrens.edu.au"
                className="rounded-lg border border-edge bg-surface px-2 py-1 text-sm text-content"
              />
            </label>
            {(dateFrom || dateTo || actorEmail) && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                  setActorEmail("");
                }}
                className="rounded-lg border border-edge px-3 py-1.5 text-sm text-content-muted hover:bg-surface-hover"
              >
                Clear
              </button>
            )}
          </div>

          {data.changes.length === 0 ? (
            <EmptyState
              title="No changes recorded"
              description={
                dateFrom || dateTo || actorEmail
                  ? "No activity matches these filters. Widen the date range or clear the actor."
                  : showSystem
                    ? "No activity has been collected against this artifact."
                    : "No human activity recorded. Microsoft and service-principal activity is hidden — use the switch above to include it."
              }
            />
          ) : (
            <DataTable data={data.changes} columns={changeColumns} initialPageSize={25} />
          )}
        </div>
      )}

      {activeTab === "exposure" &&
        (data.publicExposure.length === 0 ? (
          <EmptyState
            title="Not shared publicly"
            description="No active publish-to-web or organisation-wide link has been detected for this artifact."
          />
        ) : (
          <div className="space-y-2">
            {data.publicExposure.map((p, i) => (
              <div
                key={String(p.detectedAt || i)}
                className="rounded-xl border border-danger-border bg-danger-subtle px-4 py-3 text-sm text-danger-fg"
              >
                <span className="font-semibold">{p.shareType}</span> — first detected{" "}
                {formatDate(p.detectedAt)}, last seen {formatDate(p.lastSeenAt)}
              </div>
            ))}
          </div>
        ))}

      {activeTab === "subscriptions" &&
        (data.subscriptions.length === 0 ? (
          <EmptyState
            title="No subscriptions"
            description="Nobody receives this artifact on a schedule by email."
          />
        ) : (
          <div className="space-y-2">
            {data.subscriptions.map((s, i) => (
              <section
                key={String(s.subscriptionId || i)}
                className="rounded-xl border border-edge bg-surface-raised p-4 text-sm"
              >
                <div className="font-semibold text-content">{s.title || "Subscription"}</div>
                <div className="text-content-muted">
                  {s.frequency || "—"} · {Array.isArray(s.users) ? s.users.length : 0}{" "}
                  recipient(s)
                </div>
                {Array.isArray(s.users) && s.users.length > 0 && (
                  <div className="mt-1 break-all text-xs text-content-muted">
                    {s.users.join(", ")}
                  </div>
                )}
              </section>
            ))}
          </div>
        ))}

      {activeTab === "reports" &&
        (data.relatedReports.length === 0 ? (
          <EmptyState
            title="No reports use this dataset"
            description="No collected report in this workspace points at this dataset. A dataset with no report is a candidate for decommissioning."
          />
        ) : (
          <div className="space-y-2">
            {data.relatedReports.map((r) => (
              <button
                key={r.reportId}
                type="button"
                onClick={() =>
                  navigate(
                    `/workspaces/${data.workspaceId}/artifacts/report/${encodeURIComponent(r.reportId)}`
                  )
                }
                className="w-full rounded-xl border border-edge bg-surface-raised px-4 py-3 text-left text-sm font-medium text-content hover:bg-surface-hover"
              >
                {r.name || r.reportId}
              </button>
            ))}
          </div>
        ))}
    </div>
  );
}
